import React from 'react';
import { founderDashboardAPI } from '../../services/api';
import { useFeatureAnalytics } from '../../hooks/useFeatureAnalytics';
import FilterBar from '../../components/feature-analytics/FilterBar';
import KPICard from '../../components/founder-dashboard/KPICard';
import TrendChart from '../../components/founder-dashboard/TrendChart';
import DistributionChart from '../../components/founder-dashboard/DistributionChart';
import DataTable from '../../components/founder-dashboard/DataTable';
import RefreshIndicator from '../../components/founder-dashboard/RefreshIndicator';
import { ClipboardDocumentCheckIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';

export default function AttendanceAnalyticsPage() {
  const {
    data, loading, error, showError, lastUpdated, fetchData,
    organizationId, campusId, groupId, handleFilterChange
  } = useFeatureAnalytics(founderDashboardAPI.getAttendanceAnalytics);

  if (showError) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <p className="text-red-800 mb-4">{error}</p>
        <button onClick={fetchData} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">
          Retry
        </button>
      </div>
    );
  }

  const { kpis = {}, trends = [], distribution = {}, table = [] } = data || {};
  const healthDist = kpis.groupHealthDistribution || {};

  const columns = [
    { key: 'name', header: 'Group Name', sortable: true },
    { key: 'organizationName', header: 'Organization', sortable: true },
    {
      key: 'attendanceRate',
      header: 'Attendance Rate',
      sortable: true,
      render: (value) => {
        const pct = typeof value === 'number' ? (value * 100).toFixed(1) : 'N/A';
        const color = value < 0.6 ? 'text-red-600' : value < 0.8 ? 'text-amber-600' : 'text-green-600';
        return <span className={color}>{pct}%</span>;
      }
    },
    {
      key: 'atRiskMembers',
      header: 'At-Risk',
      sortable: true,
      render: (value) => (
        <span className={value > 0 ? 'text-amber-600 font-medium' : ''}>
          {value || 0}
        </span>
      )
    },
    {
      key: 'lastMeetingDate',
      header: 'Last Meeting',
      sortable: true,
      render: (value) => value ? new Date(value).toLocaleDateString() : 'N/A'
    }
  ];

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <ClipboardDocumentCheckIcon className="h-6 w-6 text-blue-600" />
          <div>
            <h2 className="text-xl font-semibold text-slate-800">Attendance Analytics</h2>
            <p className="text-slate-600 text-sm">Attendance tracking, group health, and at-risk members</p>
          </div>
        </div>
        <RefreshIndicator lastUpdated={lastUpdated} onRefresh={fetchData} loading={loading} />
      </div>

      <FilterBar
        organizationId={organizationId}
        campusId={campusId}
        groupId={groupId}
        onFilterChange={handleFilterChange}
      />

      {/* KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <KPICard
          title="Adoption Rate"
          value={kpis.adoption || 0}
          format="percent"
          loading={loading && !data}
        />
        <KPICard
          title="Overall Attendance"
          value={kpis.attendanceRate || 0}
          format="percent"
          loading={loading && !data}
        />
        <KPICard
          title="At-Risk Members"
          value={kpis.atRiskMembers || 0}
          icon={ExclamationTriangleIcon}
          loading={loading && !data}
        />
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <p className="text-sm text-gray-500 uppercase tracking-wide font-medium mb-2">Group Health</p>
          <div className="flex gap-4 text-lg">
            <span className="text-emerald-600 font-semibold">{healthDist.healthy || 0} 🟢</span>
            <span className="text-amber-600 font-semibold">{healthDist.needsAttention || 0} 🟡</span>
            <span className="text-red-600 font-semibold">{healthDist.atRisk || 0} 🔴</span>
          </div>
          <p className="text-sm text-gray-400 mt-2">By attendance rate</p>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <h3 className="text-lg font-medium text-slate-800 mb-4">Weekly Attendance Trends</h3>
          <TrendChart
            data={trends}
            lines={[
              { key: 'present', name: 'Present', color: '#22C55E' },
              { key: 'absent', name: 'Absent', color: '#EF4444' }
            ]}
            type="area"
            height={250}
          />
        </div>

        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <h3 className="text-lg font-medium text-slate-800 mb-4">Group Health Distribution</h3>
          <DistributionChart
            data={{
              Healthy: healthDist.healthy || 0,
              'Needs Attention': healthDist.needsAttention || 0,
              'At Risk': healthDist.atRisk || 0
            }}
            showPercentage={false}
            height={250}
          />
        </div>
      </div>

      {/* Check-in Methods */}
      {distribution.checkInMethods && Object.keys(distribution.checkInMethods).length > 0 && (
        <div className="bg-white rounded-lg border border-slate-200 p-4 mb-8">
          <h3 className="text-lg font-medium text-slate-800 mb-4">Check-in Methods</h3>
          <DistributionChart
            data={distribution.checkInMethods}
            showPercentage={true}
            height={200}
          />
        </div>
      )}

      {/* Groups Needing Attention Table */}
      <div className="bg-white rounded-lg border border-slate-200 p-4">
        <h3 className="text-lg font-medium text-slate-800 mb-4">Groups Needing Attention</h3>
        <DataTable
          data={table}
          columns={columns}
          loading={loading && !data}
          emptyMessage="No groups needing attention"
        />
      </div>
    </div>
  );
}
